import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import {
  createCubicBezier,
  type CubicBezier,
  getPointOnCubicBezier,
  type ReadonlyCubicBezier,
} from '@canvas/bezier';
import { calculateGlobalPosition, calculateRelativePosition } from '@canvas/math/point';

import {
  type Line,
  type CubicBezierDefinitionPoints,
  type PathSegment,
  type PathSegmentCubicBezier,
  type PathSegmentMetadata,
} from '../types';

/**
 * User type guard to check if a PathSegment is a PathSegmentCubicBezier
 *
 * @param segment
 * @returns boolean, true if PathSegmentCubicBezier
 */
export function isCubicBezierSegment(
  segment: PathSegment
): segment is PathSegmentCubicBezier {
  return segment.type === 'cubic-bezier';
}

/**
 * Convenience function for constructing PathSegmentCubicBeziers
 * All coordinates are relative to the implied start point at 0,0
 *
 * @param control1 position of the first control point relative to the start point
 * @param control2 position of the second control point relative to the start point
 * @param end position of the end point relative to the start point
 * @param pathHandleMetadata - metadata about path handle
 */
export function createCubicBezierSegment(
  control1: vec2,
  control2: vec2,
  end: vec2,
  pathSegmentMetadata?: PathSegmentMetadata
): PathSegmentCubicBezier {
  return {
    type: 'cubic-bezier',
    control1,
    control2,
    end,
    pathSegmentMetadata,
  };
}

/**
 * Given a cubic bezier path segment and a start point,
 * this derives a CubicBezier
 *
 * @param segment PathSegmentCubicBezier
 * @param start optional ReadonlyVec2, omit this if start is always [0,0] to save on computation
 * @returns CubicBezier
 */
export function createCubicBezierFromSegment(
  segment: PathSegmentCubicBezier,
  start?: ReadonlyVec2
): CubicBezier {
  if (!start) {
    return createCubicBezier(
      vec2.create(),
      segment.control1,
      segment.control2,
      segment.end
    );
  }

  return createCubicBezier(
    vec2.clone(start),
    vec2.add(vec2.create(), start, segment.control1),
    vec2.add(vec2.create(), start, segment.control2),
    vec2.add(vec2.create(), start, segment.end)
  );
}

/**
 * Given an array of cubic beziers
 * which are assumed to be continuous,
 * this produces equivalent an array of PathSegmentCubicBeziers
 *
 * @param beziers CubicBezier[]
 * @returns PathSegmentCubicBezier[]
 */
export function createSegmentsFromCubicBeziers(
  beziers: Array<{ bezier: CubicBezier; pathSegmentMetadata?: PathSegmentMetadata }>
): PathSegmentCubicBezier[] {
  if (beziers.length === 0) {
    return [];
  }

  const segments: PathSegmentCubicBezier[] = [];
  const cursor = vec2.clone(beziers[0].bezier.start);

  // We want to iterate through each bezier and create a segment
  for (let index = 0; index < beziers.length; index++) {
    const { bezier, pathSegmentMetadata } = beziers[index];
    const control1 = vec2.sub(vec2.create(), bezier.control1, cursor);
    const control2 = vec2.sub(vec2.create(), bezier.control2, cursor);
    const end = vec2.sub(vec2.create(), bezier.end, cursor);
    segments.push(createCubicBezierSegment(control1, control2, end, pathSegmentMetadata));

    // If there is a next segment, we want to use its start as the new cursor
    // position so we can continue to build a continuous path
    if (index + 1 <= beziers.length - 1) {
      vec2.copy(cursor, beziers[index + 1].bezier.start);
    }
  }

  return segments;
}

/**
 * Converts an array of cubic Bezier curves with global (absolute) coordinates
 * to our internal data model represented using a local coordinate system.
 *
 * @param curves The array of cubic Bezier curves represented using global coordinates.
 * @returns The array of curves in the local coordinate system.
 */
export function convertCubicBeziersToLocalCoordinateSystem(
  curves: CubicBezierDefinitionPoints[]
): PathSegmentCubicBezier[] {
  return curves.map((curve, index) => {
    // The start point for the first curve is its own start point.
    // For subsequent curves, it's the end point of the previous curve.
    const start = index === 0 ? curve[0] : curves[index - 1][3];
    const control1 = calculateRelativePosition(curve[1], start);
    const control2 = calculateRelativePosition(curve[2], start);
    const end = calculateRelativePosition(curve[3], start);
    return createCubicBezierSegment(control1, control2, end);
  });
}

/**
 * Converts a single cubic Bezier segment back to a global coordinate system.
 * When traversing a path containing cubic Beziers, we use these global
 * coordinates to compute the constituent line segments that will approximate
 * the Bezier curve for rendering.
 *
 * @param segment The cubic Bezier segment represented in a local coordinate system.
 * @param start The starting point of the segment in the global coordinate system.
 * @returns
 */
export function convertCubicBezierToGlobalCoordinateSystem(
  segment: PathSegmentCubicBezier,
  start: vec2
): CubicBezier {
  const control1 = calculateGlobalPosition(segment.control1, start);
  const control2 = calculateGlobalPosition(segment.control2, start);
  const end = calculateGlobalPosition(segment.end, start);
  return {
    start,
    control1,
    control2,
    end,
  };
}

const SUBDIVISIONS = 20;

export const convertCubicBezierPointsToLines = (bezier: ReadonlyCubicBezier) => {
  const lines: Line[] = [];

  const start = vec2.clone(bezier.start);
  const end = vec2.create();

  for (let i = 1; i <= SUBDIVISIONS; i++) {
    getPointOnCubicBezier(end, bezier, i / SUBDIVISIONS);

    lines.push({
      start: vec2.clone(start),
      end: vec2.clone(end),
    });

    vec2.copy(start, end);
  }

  return lines;
};
